/*
  Care Guide section — hover-zone horizontal scroll

  Desktop (≥ 990px, fine pointer): the card track scrolls horizontally
  as the mouse hovers in the left or right edge zone of the track.
  Speed scales with how close the cursor is to the edge — gentle near
  the inner boundary, faster near the outer edge. Cursor in the
  middle = no auto-scroll. Native horizontal scrolling (trackpad/wheel)
  still works alongside.

  Mobile / coarse pointer: no auto-scroll; users swipe horizontally
  via native overflow-x. Disabling on coarse pointers prevents touch
  pans from triggering edge-scroll when the user is just trying to
  scroll the page.

  Anchor links jump to the target card from any breakpoint via
  `scrollTo` on the cards container, clamped to the max scroll
  position so late anchors don't overshoot the last fully-visible
  card position.
*/

(function () {
  // Right and left auto-scroll edge zones, as a fraction of the track
  // width on each side. Inside that zone the scroll EASES toward a target
  // speed that scales with distance from the inner boundary toward the edge.
  var HOVER_ZONE = 0.2;
  // Top speed (pixels-per-frame, ~60fps) reached at the very edge.
  var MAX_SPEED = 14;
  // Velocity easing per frame toward the target — smaller = smoother / lazier.
  var ACCEL = 0.07;
  // Curve on edge intensity — >1 softens the onset near the inner boundary.
  var CURVE = 1.7;
  // Below this speed with no target, the glide is considered settled.
  var SETTLE = 0.05;

  function getCards(section) {
    return section.querySelector('[data-care-guide-cards]');
  }

  function CareGuide(section) {
    this.section = section;
    this.cards = getCards(section);
    this.track = section.querySelector('[data-care-guide-track]');
    this.anchors = section.querySelectorAll('[data-care-guide-anchor]');
    this.velocity = 0; // current eased speed
    this.target = 0; // desired speed from the cursor position
    this.pos = 0; // sub-pixel scroll accumulator
    this.raf = null;
    this.equaliseTimer = null;

    if (!this.cards) return;

    this.bindAnchorClicks();
    this.bindHoverScroll();
    this.equaliseDescriptionHeights();
    this.bindEqualiseOnResize();

    /* Re-equalise once webfonts have loaded — font-swap changes line
       wrapping and can grow or shrink any description's natural
       height. Without this, the initial pass runs against the system
       font, then the swap repaints with new line counts and the
       carefully-equalised heights misalign again. */
    var self = this;
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(function () { self.equaliseDescriptionHeights(); });
    }
  }

  /**
   * Make every `.care-guide__card-description` in this section the same
   * height as the tallest one — so the accordions sitting below them
   * (Do's / Don'ts) line up across cards regardless of how long each
   * description is. CSS provides a sensible fallback `min-height` but
   * merchants editing copy in the customiser will easily exceed it; this
   * measures at runtime so the layout adapts to whatever they enter.
   *
   * Method: temporarily clear any min-height so we measure natural
   * content height, find the max, then apply that max as min-height to
   * every description.
   */
  CareGuide.prototype.equaliseDescriptionHeights = function () {
    var descs = this.section.querySelectorAll('.care-guide__card-description');
    if (!descs.length) return;

    // Reset first so a resize from wider→narrower viewport doesn't keep
    // the old (too-tall) value as a sticky floor.
    descs.forEach(function (el) { el.style.minHeight = ''; });

    var max = 0;
    descs.forEach(function (el) {
      // offsetHeight reads natural rendered height with min-height removed.
      var h = el.offsetHeight;
      if (h > max) max = h;
    });

    if (max <= 0) return;

    descs.forEach(function (el) { el.style.minHeight = max + 'px'; });
  };

  /**
   * Re-run the equaliser on resize since line wraps change with width.
   * Debounced so a window drag doesn't churn through dozens of layout
   * passes per second.
   */
  CareGuide.prototype.bindEqualiseOnResize = function () {
    var self = this;
    window.addEventListener('resize', function () {
      if (self.equaliseTimer) clearTimeout(self.equaliseTimer);
      self.equaliseTimer = setTimeout(function () {
        self.equaliseDescriptionHeights();
      }, 150);
    }, { passive: true });
  };

  CareGuide.prototype.bindAnchorClicks = function () {
    var self = this;
    this.anchors.forEach(function (anchor) {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        var targetId = anchor.getAttribute('data-care-guide-anchor');
        var target = self.section.querySelector('[data-care-guide-card="' + targetId + '"]');
        if (!target) return;

        var first = self.cards.firstElementChild;
        var firstOffset = first ? first.offsetLeft : 0;
        var travel = target.offsetLeft - firstOffset;
        // Clamp so late anchors don't overshoot the max-scroll position.
        var max = self.cards.scrollWidth - self.cards.clientWidth;
        var travelClamped = Math.max(0, Math.min(travel, max));

        if (typeof self.cards.scrollTo === 'function') {
          self.cards.scrollTo({ left: travelClamped, behavior: 'smooth' });
        } else {
          self.cards.scrollLeft = travelClamped;
        }
      });
    });
  };

  CareGuide.prototype.bindHoverScroll = function () {
    if (!this.track) return;
    // Only desktop with a fine pointer (mouse/trackpad). Touch and
    // coarse-pointer devices use native scroll/swipe; auto-scroll
    // there would conflict with the page's vertical pan gesture.
    var fine = window.matchMedia('(pointer: fine)').matches;
    var desktop = window.matchMedia('(min-width: 990px)').matches;
    if (!fine || !desktop) return;

    var self = this;

    this.track.addEventListener('mousemove', function (e) {
      var rect = self.track.getBoundingClientRect();
      var relX = (e.clientX - rect.left) / rect.width; // 0..1 across the track

      if (relX < HOVER_ZONE) {
        // Left zone: intensity 0 at the inner boundary, 1 at the very edge.
        var intensityLeft = (HOVER_ZONE - relX) / HOVER_ZONE;
        self.target = -Math.pow(intensityLeft, CURVE) * MAX_SPEED;
      } else if (relX > 1 - HOVER_ZONE) {
        var intensityRight = (relX - (1 - HOVER_ZONE)) / HOVER_ZONE;
        self.target = Math.pow(intensityRight, CURVE) * MAX_SPEED;
      } else {
        self.target = 0;
      }

      self.kickAnimation();
    });

    // Glide to rest rather than hard-stopping when the cursor leaves.
    this.track.addEventListener('mouseleave', function () {
      self.target = 0;
      self.kickAnimation();
    });

    // Re-evaluate on resize: if the track shrinks below desktop width
    // we should stop driving the scroll (mobile takes over via swipe).
    window.addEventListener('resize', function () {
      var stillEligible = window.matchMedia('(pointer: fine)').matches && window.matchMedia('(min-width: 990px)').matches;
      if (!stillEligible) self.target = 0;
    });
  };

  CareGuide.prototype.kickAnimation = function () {
    if (this.raf) return;
    var self = this;
    self.pos = self.cards.scrollLeft; // re-sync before a fresh glide

    var tick = function () {
      // Ease the current speed toward the target.
      self.velocity += (self.target - self.velocity) * ACCEL;

      // Settled: no target and effectively stopped.
      if (self.target === 0 && Math.abs(self.velocity) < SETTLE) {
        self.velocity = 0;
        self.raf = null;
        return;
      }

      var max = self.cards.scrollWidth - self.cards.clientWidth;
      self.pos += self.velocity;

      // Clamp + park at the boundaries; stop the loop if jammed into one.
      if (self.pos <= 0) {
        self.pos = 0;
        self.velocity = 0;
        self.cards.scrollLeft = 0;
        if (self.target <= 0) {
          self.raf = null;
          return;
        }
      } else if (self.pos >= max) {
        self.pos = max;
        self.velocity = 0;
        self.cards.scrollLeft = max;
        if (self.target >= 0) {
          self.raf = null;
          return;
        }
      } else {
        self.cards.scrollLeft = self.pos;
      }

      self.raf = window.requestAnimationFrame(tick);
    };

    this.raf = window.requestAnimationFrame(tick);
  };

  function init() {
    document.querySelectorAll('[data-care-guide]').forEach(function (section) {
      if (section._careGuideInit) return;
      section._careGuideInit = true;
      new CareGuide(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-care-guide]')) {
      init();
    }
  });
})();
